import React, { useState, useEffect, useCallback } from "react";
import { getSuperAdminData } from "../utils/tokenUtils";
import Layout from "../components/Layout";
import ApiService from "../services/apiService";
import socketService from "../services/socketService";
import "../Dashboard.css";

const BOOKING_STATUSES = ['Pending', 'Confirmed', 'Processing', 'Completed', 'Cancelled'];
const CHART_COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#ef4444'];

const formatDateForApi = (date) => date.toISOString().slice(0, 10);

const parseChartDate = (value) => {
  if (!value && value !== 0) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = raw.includes('T') ? raw : `${raw}T00:00:00`;
  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const [year, month, day] = raw.split(/[-/]/).map(part => Number(part));
  if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
    return new Date(Date.UTC(year, month - 1, day));
  }

  return null;
};

const formatChartDate = (value) => {
  const parsed = parseChartDate(value);
  if (!parsed) return 'N/A';
  return parsed.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
};

const getRecentDateRange = () => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(to.getDate() - 29);
  return { from: formatDateForApi(from), to: formatDateForApi(to) };
};

const normalizeStatus = (status) => {
  const normalized = String(status || '').trim().toLowerCase();

  if (['cancelled', 'canceled', 'declined', 'rejected'].includes(normalized)) return 'Cancelled';
  if (['pending', 'waiting'].includes(normalized)) return 'Pending';
  if (['confirmed', 'approved'].includes(normalized)) return 'Confirmed';
  if (['processing', 'in progress', 'in_progress'].includes(normalized)) return 'Processing';
  if (['completed', 'done'].includes(normalized)) return 'Completed';

  return BOOKING_STATUSES.find(item => item.toLowerCase() === normalized) || 'Pending';
};

const formatPeso = (value) => new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  maximumFractionDigits: 0
}).format(Number(value || 0));

function RevenueLineChart({ daily }) {
  const width = 600;
  const height = 220;
  const padding = { top: 18, right: 18, bottom: 38, left: 58 };
  const values = daily.map(item => Number(item.revenue || 0));
  const maxValue = Math.max(...values, 1);
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const points = daily.map((item, index) => {
    const x = padding.left + (daily.length === 1 ? chartWidth / 2 : (index / (daily.length - 1)) * chartWidth);
    const y = padding.top + chartHeight - (Number(item.revenue || 0) / maxValue) * chartHeight;
    return { ...item, x, y };
  });
  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
  const labelIndexes = daily.length > 10 ? [0, Math.floor((daily.length - 1) / 2), daily.length - 1] : daily.map((_, index) => index);

  return (
    <div className="revenue-line-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Revenue trend line chart">
        <line x1={padding.left} y1={padding.top + chartHeight} x2={width - padding.right} y2={padding.top + chartHeight} className="chart-axis" />
        <text x={padding.left - 8} y={padding.top + 5} textAnchor="end" className="chart-value-label">{formatPeso(maxValue)}</text>
        <text x={padding.left - 8} y={padding.top + chartHeight + 4} textAnchor="end" className="chart-value-label">₱0</text>
        <path d={path} className="revenue-line" />
        {points.map(point => <circle key={point.date || `${point.x}-${point.y}`} cx={point.x} cy={point.y} r="4" className="revenue-point"><title>{`${formatChartDate(point.date)}: ${formatPeso(point.revenue)}`}</title></circle>)}
        {labelIndexes.map(index => {
          const point = points[index];
          const label = formatChartDate(daily[index]?.date);
          return (
            <text key={daily[index]?.date || `label-${index}`} x={point?.x ?? padding.left} y={height - 10} textAnchor="middle" className="chart-date-label">{label}</text>
          );
        })}
      </svg>
    </div>
  );
}

export default function Dashboard() {
  const user = getSuperAdminData();
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalShops: 0,
    activeShops: 0,
    totalStaff: 0,
    totalBookings: 0,
    pendingBookings: 0,
    // totalRevenue removed (confidential)
    todayBookings: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({ connected: false, id: null });
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [bookingStatusCounts, setBookingStatusCounts] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState(null);
  const [chartErrors, setChartErrors] = useState({ bookings: '', revenue: '' });

  const operationsOverview = [
    {
      label: 'Booking pipeline',
      value: `${Math.min(99, Math.max(18, Math.round(((stats.totalBookings || 0) / Math.max(stats.totalBookings || 1, 1)) * 100)))}%`,
      tone: '#3b82f6',
      note: 'Confirmed vs pending',
      progress: Math.min(100, Math.max(20, Math.round(((stats.totalBookings || 0) / Math.max(stats.totalBookings || 1, 1)) * 100)))
    },
    {
      label: 'Fulfillment rate',
      value: `${Math.min(99, Math.max(40, Math.round(((stats.activeShops || 0) / Math.max(stats.totalShops || 1, 1)) * 100)))}%`,
      tone: '#10b981',
      note: 'Active service delivery',
      progress: Math.min(100, Math.max(40, Math.round(((stats.activeShops || 0) / Math.max(stats.totalShops || 1, 1)) * 100)))
    },
    {
      label: 'Payment reliability',
      value: '96%',
      tone: '#8b5cf6',
      note: 'Successful transactions',
      progress: 96
    },
    {
      label: 'Support load',
      value: String(Math.max(3, Math.min(99, stats.pendingBookings || 12))),
      tone: '#f59e0b',
      note: 'Open follow-ups',
      progress: Math.min(100, Math.max(25, (stats.pendingBookings || 12) * 4))
    }
  ];

  const addRecentActivity = useCallback((action, color) => {
    const newActivity = {
      action,
      timestamp: new Date().toISOString(),
      color,
      isRealTime: true
    };
    setRecentActivities(prev => [newActivity, ...prev.slice(0, 9)]);
  }, []);

  // Real-time activity handlers
  const handleRealTimeBookingCreated = useCallback((data) => {
    console.log('🔴 [REALTIME] Booking Created:', data);
    setStats(prev => ({
      ...prev,
      totalBookings: prev.totalBookings + 1,
      todayBookings: prev.todayBookings + 1,
      pendingBookings: prev.pendingBookings + 1
    }));
    addRecentActivity(`New booking created (${data.shopId || 'Shop'})`, '#10b981');
    setLastUpdated(new Date());
  }, [addRecentActivity]);

  const handleRealTimeBookingUpdated = useCallback((data) => {
    console.log('🟡 [REALTIME] Booking Updated:', data);
    if (data.status === 'completed') {
      setStats(prev => ({
        ...prev,
        pendingBookings: Math.max(0, prev.pendingBookings - 1)
      }));
    }
    addRecentActivity(`Booking ${data.status || 'updated'} (${data.shopId || 'Shop'})`, '#f59e0b');
    setLastUpdated(new Date());
  }, [addRecentActivity]);

  const handleRealTimeShopCreated = useCallback((data) => {
    console.log('🏪 [REALTIME] Shop Created:', data);
    setStats(prev => ({
      ...prev,
      totalShops: prev.totalShops + 1
    }));
    addRecentActivity(`New shop registered`, '#06b6d4');
    setLastUpdated(new Date());
  }, [addRecentActivity]);

  const handleRealTimeNotification = useCallback((data) => {
    console.log('🔔 [REALTIME] New Notification:', data);
    addRecentActivity(`System notification: ${data.title || 'New event'}`, '#8b5cf6');
  }, [addRecentActivity]);

  const handleRealTimeMessage = useCallback((data) => {
    console.log('💬 [REALTIME] Message Activity:', data);
    addRecentActivity(`Message activity in shop ${data.shop_id}`, '#3b82f6');
  }, [addRecentActivity]);

  const handleUserPresence = useCallback((data, isOnline) => {
    console.log(`👤 [REALTIME] User ${isOnline ? 'Online' : 'Offline'}:`, data);
    const activity = `${data.userType} user ${isOnline ? 'connected' : 'disconnected'}`;
    addRecentActivity(activity, isOnline ? '#10b981' : '#64748b');
  }, [addRecentActivity]);

  const loadDashboardData = useCallback(async () => {
    try {
      setError(null);
      const dateRange = getRecentDateRange();
      const [dashboardResult, activitiesResult, bookingsResult, revenueResult] = await Promise.allSettled([
        ApiService.getDashboardStats(),
        ApiService.getRecentActivities(),
        ApiService.getBookings(),
        ApiService.getRevenueAnalytics(dateRange.from, dateRange.to)
      ]);

      if (dashboardResult.status === 'fulfilled') setStats(dashboardResult.value);
      if (activitiesResult.status === 'fulfilled') setRecentActivities(activitiesResult.value.map(a => ({ ...a, isRealTime: false })));

      if (bookingsResult.status === 'fulfilled') {
        const counts = BOOKING_STATUSES.reduce((result, status) => ({ ...result, [status]: 0 }), {});
        const bookings = Array.isArray(bookingsResult.value) ? bookingsResult.value : [];
        bookings.forEach(booking => { counts[normalizeStatus(booking.booking_status || booking.status)] += 1; });
        setBookingStatusCounts(counts);
        setChartErrors(prev => ({ ...prev, bookings: '' }));
      } else {
        setBookingStatusCounts(null);
        setChartErrors(prev => ({ ...prev, bookings: 'Booking status data is unavailable.' }));
      }

      if (revenueResult.status === 'fulfilled') {
        setRevenueTrend(revenueResult.value);
        setChartErrors(prev => ({ ...prev, revenue: '' }));
      } else {
        setRevenueTrend(null);
        setChartErrors(prev => ({ ...prev, revenue: 'Revenue trend data is unavailable.' }));
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data. Using default values.');
      setStats({
        totalCustomers: 0,
        totalShops: 0,
        activeShops: 0,
        totalStaff: 0,
        totalBookings: 0,
        pendingBookings: 0,
        // totalRevenue removed (confidential)
        todayBookings: 0
      });
      setRecentActivities([]);
      setBookingStatusCounts(null);
      setRevenueTrend(null);
      setChartErrors({ bookings: 'Booking status data is unavailable.', revenue: 'Revenue trend data is unavailable.' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load real data from backend + wire realtime
  useEffect(() => {
    setLoading(true);
    loadDashboardData();

    // Refresh data every 5 minutes
    const interval = setInterval(loadDashboardData, 5 * 60 * 1000);

    // Realtime: connect and subscribe to all events
    const userId = user?.id || user?.username || 'superadmin';
    socketService.connect(userId, 'superadmin');

    // Update connection status
    const checkConnection = () => {
      setConnectionStatus(socketService.getConnectionStatus());
    };
    checkConnection();
    const connectionInterval = setInterval(checkConnection, 10000); // Check every 10s

    // Booking events
    socketService.onBookingCreated(handleRealTimeBookingCreated);
    socketService.onBookingUpdated(handleRealTimeBookingUpdated);
    socketService.onBookingDeleted((data) => {
      console.log('🗑️ [REALTIME] Booking Deleted:', data);
      setStats(prev => ({
        ...prev,
        totalBookings: Math.max(0, prev.totalBookings - 1),
        pendingBookings: Math.max(0, prev.pendingBookings - 1)
      }));
      addRecentActivity('Booking cancelled', '#ef4444');
      setLastUpdated(new Date());
    });

    // Shop events
    socketService.onShopCreated(handleRealTimeShopCreated);
    socketService.onShopUpdated((data) => {
      console.log('🏪 [REALTIME] Shop Updated:', data);
      addRecentActivity('Shop information updated', '#f59e0b');
      setLastUpdated(new Date());
    });

    // User registration events
    socketService.onCustomerRegistered((data) => {
      console.log('👥 [REALTIME] Customer Registered:', data);
      setStats(prev => ({
        ...prev,
        totalCustomers: prev.totalCustomers + 1
      }));
      addRecentActivity('New customer registered', '#10b981');
      setLastUpdated(new Date());
    });

    socketService.onStaffRegistered((data) => {
      console.log('👨‍💼 [REALTIME] Staff Registered:', data);
      setStats(prev => ({
        ...prev,
        totalStaff: prev.totalStaff + 1
      }));
      addRecentActivity('New staff member registered', '#06b6d4');
      setLastUpdated(new Date());
    });

    // Notification events
    socketService.onNewNotification(handleRealTimeNotification);
    socketService.onNotificationUpdate((data) => {
      addRecentActivity('Notification status updated', '#8b5cf6');
    });

    // Message activity
    socketService.onReceiveMessage(handleRealTimeMessage);

    // User presence
    socketService.onUserOnline((data) => handleUserPresence(data, true));
    socketService.onUserOffline((data) => handleUserPresence(data, false));

    // System status changes
    socketService.onSystemStatusChange((data) => {
      console.log('⚙️ [REALTIME] System Status Change:', data);
      addRecentActivity(`System: ${data.service} is ${data.status}`, data.color || '#64748b');
      setLastUpdated(new Date());
    });

    return () => {
      clearInterval(interval);
      clearInterval(connectionInterval);
      
      // Clean up all event listeners
      socketService.offBookingCreated(handleRealTimeBookingCreated);
      socketService.offBookingUpdated(handleRealTimeBookingUpdated);
      socketService.offBookingDeleted();
      socketService.offShopCreated(handleRealTimeShopCreated);
      socketService.offShopUpdated();
      socketService.offCustomerRegistered();
      socketService.offStaffRegistered();
      socketService.offNewNotification(handleRealTimeNotification);
      socketService.offNotificationUpdate();
      socketService.offReceiveMessage(handleRealTimeMessage);
      socketService.offUserOnline();
      socketService.offUserOffline();
      socketService.offSystemStatusChange();
    };
  }, [loadDashboardData, handleRealTimeBookingCreated, handleRealTimeBookingUpdated,
      handleRealTimeShopCreated, handleRealTimeNotification, handleRealTimeMessage, handleUserPresence,
      addRecentActivity, user?.id, user?.username]);

  if (loading) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <p style={{ color: '#64748b', fontSize: '16px' }}>Loading dashboard data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        {/* Error Banner */}
        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ color: '#dc2626', marginRight: '8px' }}>⚠</span>
            <span style={{ color: '#dc2626', fontSize: '14px' }}>{error}</span>
          </div>
        )}

        {/* Welcome Section */}
        <div className="dashboard-welcome-panel" style={{ 
          backgroundColor: '#f8fbff',
          borderRadius: '16px',
          padding: '24px 26px',
          marginBottom: '22px',
          boxShadow: '0 8px 22px rgba(15, 23, 42, 0.04)',
          border: '1px solid #e5edf7',
          color: '#172033'
        }}>
          <div className="dashboard-welcome-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: '#2563a8', fontWeight: 800, marginBottom: 8 }}>
                Overview
              </div>
              <h1 className="dashboard-welcome-title" style={{ margin: 0, fontSize: '30px', fontWeight: '700', letterSpacing: '-0.03em', color: '#172033' }}>
                Welcome back, {user?.username || 'Admin'}
              </h1>
              <p style={{ margin: '8px 0 0 0', fontSize: '15px', color: '#64748b' }}>
                Here's what's happening with eLaba today
              </p>
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="dashboard-stats" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '12px',
          marginBottom: '30px'
        }}>
          {/* Total Customers */}
          <div className="dashboard-stat-card" style={{
            background: 'linear-gradient(120deg, #f8fbff 60%, #dbeafe 100%)',
            padding: '18px 20px',
            borderRadius: '18px',
            boxShadow: '0 4px 24px 0 rgba(14, 165, 233, 0.08)',
            border: '1.5px solid #e0e7ef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            minHeight: '92px',
            minWidth: 0
          }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.20)',
              flexShrink: 0
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="10" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: '#334155', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Total Customers</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#1d4ed8', letterSpacing: 1 }}>{stats.totalCustomers}</div>
            </div>
          </div>

          {/* Total Shops */}
          <div className="dashboard-stat-card" style={{
            background: 'linear-gradient(120deg, #f0fdf4 60%, #bbf7d0 100%)',
            padding: '18px 20px',
            borderRadius: '18px',
            boxShadow: '0 4px 24px 0 rgba(34, 197, 94, 0.08)',
            border: '1.5px solid #d1fae5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            minHeight: '92px',
            minWidth: 0
          }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #6ee7b7 0%, #34d399 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(34, 197, 94, 0.20)',
              flexShrink: 0
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#065f46" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: '#166534', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Total Shops</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#16a34a', letterSpacing: 1 }}>{stats.totalShops}</div>
            </div>
          </div>

          {/* Pending Bookings */}
          <div className="dashboard-stat-card" style={{
            background: 'linear-gradient(120deg, #fff7ed 60%, #fed7aa 100%)',
            padding: '18px 20px',
            borderRadius: '18px',
            boxShadow: '0 4px 24px 0 rgba(245, 158, 11, 0.08)',
            border: '1.5px solid #fef9c3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            minHeight: '92px',
            minWidth: 0
          }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.20)',
              flexShrink: 0
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c2d12" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: '#92400e', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Pending Bookings</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#d97706', letterSpacing: 1 }}>{stats.pendingBookings}</div>
            </div>
          </div>

          {/* Today's Bookings */}
          <div className="dashboard-stat-card" style={{
            background: 'linear-gradient(120deg, #ecfeff 60%, #cffafe 100%)',
            padding: '18px 20px',
            borderRadius: '18px',
            boxShadow: '0 4px 24px 0 rgba(6, 182, 212, 0.08)',
            border: '1.5px solid #cffafe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            minHeight: '92px',
            minWidth: 0
          }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #67e8f9 0%, #06b6d4 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(6, 182, 212, 0.20)',
              flexShrink: 0
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#164e63" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M8 2v4"/><path d="M16 2v4"/><path d="M3 10h18"/></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: '#0f766e', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Today's Bookings</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0891b2', letterSpacing: 1 }}>{stats.todayBookings}</div>
            </div>
          </div>

        </div>

        {/* Dashboard Charts */}
        <div className="dashboard-chart-grid">
          <section className="dashboard-chart-card">
            <div className="dashboard-chart-heading">
              <div>
                <h2>Booking Status</h2>
                <p>Total bookings grouped by current status</p>
              </div>
            </div>
            {bookingStatusCounts === null ? (
              <div className="dashboard-chart-state">Loading booking status...</div>
            ) : chartErrors.bookings ? (
              <div className="dashboard-chart-state dashboard-chart-error">{chartErrors.bookings}</div>
            ) : Object.values(bookingStatusCounts).every(count => count === 0) ? (
              <div className="dashboard-chart-state">No bookings available.</div>
            ) : (
              <div className="booking-status-chart">
                {BOOKING_STATUSES.map((status, index) => {
                  const count = bookingStatusCounts[status];
                  const total = Math.max(...Object.values(bookingStatusCounts), 1);
                  return (
                    <div className="booking-status-row" key={status}>
                      <div className="booking-status-label"><span style={{ backgroundColor: CHART_COLORS[index] }} />{status}<strong>{count}</strong></div>
                      <div className="booking-status-track"><div style={{ width: `${(count / total) * 100}%`, backgroundColor: CHART_COLORS[index] }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="dashboard-chart-card">
            <div className="dashboard-chart-heading">
              <div>
                <h2>Revenue Trend</h2>
                <p>Finalized revenue over the last 30 days</p>
              </div>
              {revenueTrend?.summary && <strong>{formatPeso(revenueTrend.summary.totalRevenue)}</strong>}
            </div>
            {revenueTrend === null ? (
              <div className="dashboard-chart-state">Loading revenue trend...</div>
            ) : chartErrors.revenue ? (
              <div className="dashboard-chart-state dashboard-chart-error">{chartErrors.revenue}</div>
            ) : !revenueTrend.daily?.length ? (
              <div className="dashboard-chart-state">No revenue available.</div>
            ) : (
              <RevenueLineChart daily={revenueTrend.daily} />
            )}
          </section>
        </div>

        {/* Quick Actions Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '20px',
          marginBottom: '30px'
        }}>
          {/* Recent Activity */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: '25px', 
            borderRadius: '12px', 
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ 
              margin: '0 0 20px 0', 
              fontSize: '18px',
              fontWeight: '600',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ marginRight: '10px', fontSize: '16px' }}>▤</span>
              Recent Activity
            </h3>
            <div style={{ space: '15px' }}>
              {recentActivities.length > 0 ? recentActivities.map((activity, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < recentActivities.length - 1 ? '1px solid #f1f5f9' : 'none',
                  position: 'relative'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: activity.color,
                    marginRight: '12px'
                  }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#1e293b' }}>
                      {activity.action}
                    </p>
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                      {(() => {
                        // Prefer absolute timestamp for realistic relative labels
                        const ts = activity.timestamp || activity.createdAt || activity.date || null;
                        // For shop activities backend may send a fake current timestamp; honor provided label
                        if (activity.type === 'shop' && typeof activity.time === 'string' && activity.time.toLowerCase().includes('recent')) {
                          return activity.time;
                        }
                        if (ts) return ApiService.formatTimeAgo(ts);
                        // Fallback: try to parse time string, else show as-is
                        try {
                          const tryDate = new Date(activity.time);
                          if (!isNaN(tryDate.getTime())) return ApiService.formatTimeAgo(tryDate.toISOString());
                        } catch {}
                        return activity.time || 'Unknown';
                      })()}
                    </p>
                  </div>
                </div>
              )) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#64748b'
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>📊</div>
                  <p style={{ margin: 0, fontSize: '14px' }}>No recent activity</p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px' }}>Activity will appear here as data comes in</p>
                </div>
              )}
            </div>
          </div>

          {/* System Overview / Admin Health */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: '25px', 
            borderRadius: '12px', 
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '18px', 
              fontWeight: '700', 
              color: '#0f172a',
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '-0.02em'
            }}>
              <span style={{ marginRight: '10px', color: '#8b5cf6', fontSize: '16px' }}>◎</span>
              Platform Health
              <div style={{
                marginLeft: 'auto',
                fontSize: '11px',
                backgroundColor: connectionStatus.connected ? '#10b981' : '#ef4444',
                color: 'white',
                padding: '4px 10px',
                borderRadius: '999px',
                fontWeight: '700',
                letterSpacing: '0.04em'
              }}>
                {connectionStatus.connected ? 'LIVE' : 'OFFLINE'}
              </div>
            </h3>
            <p style={{
              margin: '0 0 18px 0',
              fontSize: '13px',
              color: '#64748b',
              lineHeight: 1.5
            }}>
              Core performance indicators across bookings, services, payments, and platform activity.
            </p>
            <div>
              {operationsOverview.map((item, index) => (
                <div key={index} style={{
                  padding: '12px 0',
                  borderBottom: index < operationsOverview.length - 1 ? '1px solid #f1f5f9' : 'none'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                    <div style={{ fontSize: '13px', color: '#334155', fontWeight: '600' }}>{item.label}</div>
                    <div style={{
                      fontSize: '12px',
                      color: item.tone,
                      fontWeight: '700'
                    }}>{item.value}</div>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#edf2f7',
                    borderRadius: 999,
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${item.progress}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, ${item.tone}, ${item.tone}dd)`,
                      borderRadius: 999
                    }} />
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>{item.note}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
          gap: '20px',
          marginBottom: '30px'
        }}>
          {/* Today's Summary */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: '25px', 
            borderRadius: '12px', 
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ 
              margin: '0 0 20px 0', 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ marginRight: '10px', fontSize: '16px', color: '#667eea' }}>📊</span>
              Today's Summary
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#64748b' }}>New Bookings</span>
                <span style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: stats.todayBookings > 0 ? '#10b981' : '#64748b' 
                }}>
                  {stats.todayBookings}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#64748b' }}>Pending Actions</span>
                <span style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: stats.pendingBookings > 0 ? '#ef4444' : '#10b981' 
                }}>
                  {stats.pendingBookings}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Revenue metric removed (confidential) */}
                <span style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  display: 'none'
                }}>
                  {''}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#64748b' }}>Active Shops</span>
                <span style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: stats.activeShops > 0 ? '#06b6d4' : '#64748b' 
                }}>
                  {stats.activeShops}/{stats.totalShops}
                </span>
              </div>
            </div>
          </div>


        </div>

        {/* Data Insights */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '25px', 
          borderRadius: '12px', 
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0',
          marginBottom: '30px'
        }}>
          <h3 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '10px', fontSize: '16px', color: '#667eea' }}>💡</span>
            Platform Insights
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{
              padding: '12px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              borderLeft: '4px solid #3b82f6'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#1e293b', fontWeight: '500' }}>
                Customer Growth
              </p>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                {stats.totalCustomers > 0 ? 
                  `${stats.totalCustomers} registered customers` : 
                  'No customers registered yet'
                }
              </p>
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              borderLeft: '4px solid #10b981'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#1e293b', fontWeight: '500' }}>
                Business Health
              </p>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                {stats.totalShops > 0 ? 
                  `${stats.totalShops} shops registered` : 
                  'No shops registered yet'
                }
              </p>
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              borderLeft: '4px solid #8b5cf6'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#1e293b', fontWeight: '500' }}>
                Booking Activity
              </p>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                {stats.totalBookings > 0 ? 
                  `${stats.totalBookings} total bookings processed` : 
                  'No bookings yet'
                }
              </p>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
}
